/* eslint-disable no-console */
import {
    Color,
    Matrix4,
    Quaternion,
    Vector3,
    BatchedMesh,
    MeshBasicMaterial,
    MeshPhysicalMaterial,
    DoubleSide,
    BufferAttribute,
    DataTexture
} from 'three';

import { LayerType } from 'app/modules/wallforming/components/scenes/camera-controls/types/layerType';
import { BatchedMeshTarget } from 'app/modules/wallforming/components/scenes/objects/articles/types/batchedMeshTarget';
import { BatchedMeshType } from 'app/modules/wallforming/components/scenes/objects/articles/types/batchedMeshType';
import {
    getSourceTypeDebugColor,
    updateFullQuaternion
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';

import type { ArticleGeometrySlots } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleGeometrySlots';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { ArticleMesh } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleMesh';
import type { BatchedMeshMaps } from 'app/modules/wallforming/components/scenes/objects/articles/types/batchedMeshMaps';
import type { BatchedMeshSet } from 'app/modules/wallforming/components/scenes/objects/articles/types/batchedMeshSet';
import type { InstanceBatchIds } from 'app/modules/wallforming/components/scenes/objects/articles/types/instanceBatchIds';
import type { MaterialQuality } from 'app/modules/wallforming/components/setting-container/types/graphicSettings';

const MAX_LOD_LEVELS = 3;
const LOD_SUFFIX_REGEX = /(?:[_\-\s]?lod[_\-\s]?([0-9]+)|[_\-\s]l([0-9]+))$/i;
const hoverColor = new Color('#86cbe4');

// ─── Capacity constants ───────────────────────────────────────────────────────
const PHYSICAL_MAX_INSTANCES = 40_000;
const PHYSICAL_MAX_VERTICES = 500_000;
const PHYSICAL_MAX_INDICES = 1_000_000;
const SHEET_MAX_INSTANCES = 40_000;
const SHEET_MAX_VERTICES = 250_000;
const SHEET_MAX_INDICES = 500_000;
const HOLES_MAX_INSTANCES = 40_000;
const HOLES_MAX_VERTICES = 250_000;
const HOLES_MAX_INDICES = 500_000;

/**
 * Write a per-instance opacity value to the batch color texture alpha channel.
 * Must be called after `setColorAt` (which only writes RGB, leaving alpha intact).
 */
export function setBatchInstanceOpacity(batch: BatchedMesh, instanceId: number, opacity: number) {
    // Access the internal color data texture
    const texture = (batch as unknown as { _colorsTexture: DataTexture | null })._colorsTexture;
    if (!texture?.image?.data) {
        return;
    }

    texture.image.data[instanceId * 4 + 3] = opacity;
    texture.needsUpdate = true;
}

/**
 * Apply a per-instance opacity shader to a BatchedMesh material.
 * Reads the alpha channel from the batch color texture (_colorsTexture)
 * and multiplies it into diffuseColor.a in the fragment shader.
 *
 * Call `setBatchInstanceOpacity()` to write per-instance alpha values.
 */
export function initializeBatchOpacity(material: MeshPhysicalMaterial) {
    material.transparent = true;
    material.depthWrite = true;

    material.onBeforeCompile = (shader) => {
        // Add a varying to pass the batch color alpha from vertex → fragment
        shader.vertexShader = shader.vertexShader.replace(
            '#include <common>',
            `#include <common>
            varying float vBatchOpacity;`
        );

        // After the batching color is read, also read the alpha channel
        shader.vertexShader = shader.vertexShader.replace(
            '#include <color_vertex>',
            `#include <color_vertex>
            #ifdef USE_BATCHING_COLOR
                float batchIdx = getIndirectIndex( gl_DrawID );
                int cSize = textureSize( batchingColorTexture, 0 ).x;
                int cj = int( batchIdx );
                int cx = cj % cSize;
                int cy = cj / cSize;
                vBatchOpacity = texelFetch( batchingColorTexture, ivec2( cx, cy ), 0 ).a;
            #else
                vBatchOpacity = 1.0;
            #endif`
        );

        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <common>',
            `#include <common>
            varying float vBatchOpacity;`
        );

        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <opaque_fragment>',
            `diffuseColor.a *= vBatchOpacity;
            #include <opaque_fragment>`
        );
    };

    material.customProgramCacheKey = () => 'batch-opacity-v1';
    material.needsUpdate = true;
}

export function applyMaterialQuality(material: MeshPhysicalMaterial, quality: MaterialQuality, isSheet = false): void {
    if (isSheet) {
        switch (quality) {
            case 'basic':
                material.roughness = 1;
                material.metalness = 0;
                material.clearcoat = 0;
                material.clearcoatRoughness = 0;
                material.envMapIntensity = 0;
                material.sheen = 0;
                break;
            case 'standard':
                material.roughness = 0.78;
                material.metalness = 0;
                material.clearcoat = 0;
                material.clearcoatRoughness = 0;
                material.envMapIntensity = 0.2;
                material.sheen = 0;
                break;
            case 'physical':
                material.roughness = 0.78;
                material.metalness = 0;
                material.clearcoat = 0;
                material.clearcoatRoughness = 0;
                material.envMapIntensity = 0.5;
                material.sheen = 0.3;
                material.sheenRoughness = 0.6;
                material.sheenColor.set('#c8a96e');
                break;
        }
    } else {
        switch (quality) {
            case 'basic':
                material.metalness = 0;
                material.roughness = 1;
                material.clearcoat = 0;
                material.clearcoatRoughness = 0;
                material.envMapIntensity = 0;
                material.sheen = 0;
                break;
            case 'standard':
                material.metalness = 0.1;
                material.roughness = 0.7;
                material.clearcoat = 0;
                material.clearcoatRoughness = 0;
                material.envMapIntensity = 0.4;
                material.sheen = 0;
                break;
            case 'physical':
                material.metalness = 0.15;
                material.roughness = 0.5;
                material.clearcoat = 0.2;
                material.clearcoatRoughness = 0.3;
                material.envMapIntensity = 0.5;
                material.sheen = 0;
                break;
        }
    }
}

export function createPhysicalMaterial(quality: MaterialQuality): MeshPhysicalMaterial {
    const material = new MeshPhysicalMaterial({
        color: 0xffffff,
        side: DoubleSide,
        vertexColors: true,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -4
    });

    applyMaterialQuality(material, quality, false);
    initializeBatchOpacity(material);
    return material;
}

export function createSheetMaterial(quality: MaterialQuality): MeshPhysicalMaterial {
    const material = new MeshPhysicalMaterial({
        color: 0xffffff,
        side: DoubleSide
    });

    applyMaterialQuality(material, quality, true);
    return material;
}

export function createHolesMaterial(): MeshBasicMaterial {
    return new MeshBasicMaterial({
        side: DoubleSide,
        color: new Color('#666666'),
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -4
    });
}

function createBatchedMeshSetInternal(quality: MaterialQuality, target: BatchedMeshTarget): BatchedMeshSet {
    const enableRaycastLayer = target === BatchedMeshTarget.MainScene;

    const physical = new BatchedMesh(
        PHYSICAL_MAX_INSTANCES,
        PHYSICAL_MAX_VERTICES,
        PHYSICAL_MAX_INDICES,
        createPhysicalMaterial(quality)
    );
    physical.name = 'raycastable';
    physical.frustumCulled = false;
    physical.perObjectFrustumCulled = true;
    if (enableRaycastLayer) {
        physical.layers.set(LayerType.RAYCAST);
    }

    const sheet = new BatchedMesh(
        SHEET_MAX_INSTANCES,
        SHEET_MAX_VERTICES,
        SHEET_MAX_INDICES,
        createSheetMaterial(quality)
    );
    sheet.name = 'raycastable';
    sheet.frustumCulled = false;
    sheet.perObjectFrustumCulled = true;
    if (enableRaycastLayer) {
        sheet.layers.set(LayerType.RAYCAST);
    }

    const holes = new BatchedMesh(HOLES_MAX_INSTANCES, HOLES_MAX_VERTICES, HOLES_MAX_INDICES, createHolesMaterial());
    holes.name = 'raycastable';
    holes.frustumCulled = false;
    holes.perObjectFrustumCulled = true;

    if (enableRaycastLayer) {
        holes.layers.set(LayerType.RAYCAST);
    }

    return { physical, sheet, holes };
}

export function createBatchedMeshSet(quality: MaterialQuality): BatchedMeshSet {
    return createBatchedMeshSetInternal(quality, BatchedMeshTarget.MainScene);
}

export function createNavigatorBatchedMeshSet(quality: MaterialQuality): BatchedMeshSet {
    return createBatchedMeshSetInternal(quality, BatchedMeshTarget.NavigatorScene);
}

export function getMeshBaseName(partName: string): string {
    return partName.replace(LOD_SUFFIX_REGEX, '').trim();
}

export function getMeshLodLevel(partName: string): number {
    const match = partName.match(LOD_SUFFIX_REGEX);
    if (!match) {
        return 0;
    }

    const raw = match[1] ?? match[2];
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {
        return 0;
    }

    if (parsed <= 0) {
        return 0;
    }

    return Math.min(parsed, MAX_LOD_LEVELS - 1);
}

/** Categorise a mesh part by name so we know which BatchedMesh it belongs to */
export function getBatchedMeshType(partName: string): BatchedMeshType {
    const normalizedName = getMeshBaseName(partName);
    const normalizedLower = normalizedName.toLowerCase();
    if (normalizedLower === 'holes') {
        return BatchedMeshType.Holes;
    }
    if (normalizedName === 'ACWT_0-SHE-DOKA') {
        return BatchedMeshType.Sheet;
    }
    return BatchedMeshType.Physical;
}

// ─── Batch instance operations ────────────────────────────────────────────────

/** Identity matrix used when allocating a new batch instance before its real matrix is set */
const identityMatrix = new Matrix4();

/** Extract the original color from a physical-part material */
function extractColor(mesh: ArticleMesh): Color {
    const mat = mesh.material as { color?: Color };
    return mat.color ? mat.color.clone() : new Color(0xff0000);
}

const vertexColorWhite = new Color(1, 1, 1);

function applyVertexColorAttribute(mesh: ArticleMesh, color: Color): void {
    const posAttr = mesh.geometry.getAttribute('position');
    const count = posAttr.count;
    const colorData = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
        colorData[i * 3] = color.r;
        colorData[i * 3 + 1] = color.g;
        colorData[i * 3 + 2] = color.b;
    }

    mesh.geometry.setAttribute('color', new BufferAttribute(colorData, 3));
}

export function isBatchedMeshSetActive(meshes: BatchedMeshSet): boolean {
    return meshes.physical !== null;
}

export function registerGeometryInBatchedMesh(
    ctx: BatchedMeshMaps,
    articleId: string,
    meshes: ArticleMesh[],
    target: BatchedMeshTarget
): void {
    /* Ensure that geometry and instance registration keep synchronized:
       Prevent empty geometry slots when the geometry wasn't already loaded yet (async loading)
    */
    const existingSlots = ctx.geometrySlots.get(articleId);
    const hasRegisteredGeometry =
        existingSlots &&
        (existingSlots.physicalSlots.length > 0 ||
            existingSlots.sheetSlotId !== null ||
            existingSlots.holesSlots.length > 0);
    if (hasRegisteredGeometry || (existingSlots && meshes.length === 0)) {
        return;
    }

    const { meshes: batch } = ctx;
    if (!batch.physical) {
        return;
    }

    if (existingSlots) {
        const instanceIds = [...ctx.instanceBatchIds.entries()]
            .filter(([, batchIds]) => batchIds.articleId === articleId)
            .map(([instanceId]) => instanceId);
        removeInstancesFromBatchedMesh(ctx, instanceIds, target);
    }

    const slots: ArticleGeometrySlots = {
        physicalSlots: [],
        sheetSlotId: null,
        sheetLodSlotIds: [],
        sheetPartName: null,
        holesSlots: []
    };

    const groupedPhysicalMeshes = new Map<
        string,
        {
            partName: string;
            color: Color;
            byLod: Map<number, ArticleMesh>;
        }
    >();

    const groupedSheetMeshes = new Map<string, { partName: string; byLod: Map<number, ArticleMesh> }>();
    const groupedHolesMeshes = new Map<string, { partName: string; byLod: Map<number, ArticleMesh> }>();

    for (const mesh of meshes) {
        const meshType = getBatchedMeshType(mesh.name);
        const lodLevel = getMeshLodLevel(mesh.name);
        const partName = mesh.partName ?? mesh.name;
        const key = `${partName}::${getMeshBaseName(mesh.name)}`;

        if (meshType === BatchedMeshType.Sheet) {
            const existingSheet = groupedSheetMeshes.get(key);
            if (existingSheet) {
                existingSheet.byLod.set(lodLevel, mesh);
            } else {
                groupedSheetMeshes.set(key, {
                    partName,
                    byLod: new Map<number, ArticleMesh>([[lodLevel, mesh]])
                });
            }
            continue;
        }

        if (meshType === BatchedMeshType.Holes) {
            const existingHoles = groupedHolesMeshes.get(key);
            if (existingHoles) {
                existingHoles.byLod.set(lodLevel, mesh);
            } else {
                groupedHolesMeshes.set(key, {
                    partName,
                    byLod: new Map<number, ArticleMesh>([[lodLevel, mesh]])
                });
            }
            continue;
        }

        const existing = groupedPhysicalMeshes.get(key);
        if (existing) {
            existing.byLod.set(lodLevel, mesh);
        } else {
            groupedPhysicalMeshes.set(key, {
                partName,
                color: extractColor(mesh),
                byLod: new Map<number, ArticleMesh>([[lodLevel, mesh]])
            });
        }
    }

    for (const group of groupedPhysicalMeshes.values()) {
        const baseMesh = group.byLod.get(0) ?? group.byLod.values().next().value;
        if (!baseMesh) {
            continue;
        }

        try {
            applyVertexColorAttribute(baseMesh, vertexColorWhite);

            const lodSlotIds: Array<number | null> = [];
            for (let lod = 0; lod < MAX_LOD_LEVELS; lod++) {
                const lodMesh = group.byLod.get(lod);
                if (!lodMesh) {
                    lodSlotIds.push(null);
                    continue;
                }

                applyVertexColorAttribute(lodMesh, vertexColorWhite);
                const lodSlotId = batch.physical.addGeometry(lodMesh.geometry);
                lodSlotIds.push(lodSlotId);
            }

            const fallbackSlotId = lodSlotIds.find((id): id is number => typeof id === 'number');
            if (fallbackSlotId === undefined) {
                continue;
            }

            slots.physicalSlots.push({
                slotId: fallbackSlotId,
                color: group.color,
                partName: group.partName,
                lodSlotIds
            });
        } catch (e) {
            console.warn(`[${target}] Failed to register physical geometry for article ${articleId}:`, e);
        }
    }

    if (batch.sheet) {
        for (const group of groupedSheetMeshes.values()) {
            if (slots.sheetSlotId !== null) {
                continue;
            }

            const baseMesh = group.byLod.get(0) ?? group.byLod.values().next().value;
            if (!baseMesh) {
                continue;
            }

            try {
                const lodSlotIds: Array<number | null> = [];
                for (let lod = 0; lod < MAX_LOD_LEVELS; lod++) {
                    const lodMesh = group.byLod.get(lod);
                    if (!lodMesh) {
                        lodSlotIds.push(null);
                        continue;
                    }

                    const lodSlotId = batch.sheet.addGeometry(lodMesh.geometry);
                    lodSlotIds.push(lodSlotId);
                }

                const fallbackSlotId = lodSlotIds.find((id): id is number => typeof id === 'number');
                if (fallbackSlotId === undefined) {
                    continue;
                }

                slots.sheetSlotId = fallbackSlotId;
                slots.sheetLodSlotIds = lodSlotIds;
                slots.sheetPartName = group.partName;
            } catch (e) {
                console.warn(`[${target}] Failed to register sheet geometry for article ${articleId}:`, e);
            }
        }
    }

    if (batch.holes) {
        for (const group of groupedHolesMeshes.values()) {
            const baseMesh = group.byLod.get(0) ?? group.byLod.values().next().value;
            if (!baseMesh) {
                continue;
            }

            try {
                const lodSlotIds: Array<number | null> = [];
                for (let lod = 0; lod < MAX_LOD_LEVELS; lod++) {
                    const lodMesh = group.byLod.get(lod);
                    if (!lodMesh) {
                        lodSlotIds.push(null);
                        continue;
                    }

                    const lodSlotId = batch.holes.addGeometry(lodMesh.geometry);
                    lodSlotIds.push(lodSlotId);
                }

                const fallbackSlotId = lodSlotIds.find((id): id is number => typeof id === 'number');
                if (fallbackSlotId === undefined) {
                    continue;
                }

                slots.holesSlots.push({
                    slotId: fallbackSlotId,
                    partName: group.partName,
                    lodSlotIds
                });
            } catch (e) {
                console.warn(`[${target}] Failed to register holes geometry for article ${articleId}:`, e);
            }
        }
    }

    ctx.geometrySlots.set(articleId, slots);
}

export function addInstancesToBatchedMesh(
    ctx: BatchedMeshMaps,
    articleId: string,
    articleInstanceIds: string[],
    target: BatchedMeshTarget
): void {
    const slots = ctx.geometrySlots.get(articleId);
    if (!slots) {
        return;
    }

    const { meshes: batch } = ctx;
    if (!batch.physical) {
        return;
    }

    for (const articleInstanceId of articleInstanceIds) {
        if (ctx.instanceBatchIds.has(articleInstanceId)) {
            continue;
        }

        const batchIds: InstanceBatchIds = {
            articleId,
            physicalIds: [],
            sheetId: null,
            holesIds: []
        };

        try {
            // Set matrices for new batched mesh instances and keep them hidden until initialization finished
            for (const { slotId, color } of slots.physicalSlots) {
                const instId = batch.physical.addInstance(slotId);
                batch.physical.setMatrixAt(instId, identityMatrix);
                batch.physical.setVisibleAt(instId, false);
                batch.physical.setColorAt(instId, color);
                batchIds.physicalIds.push(instId);
                ctx.physicalBatchToInstance?.set(instId, articleInstanceId);
            }

            if (batch.sheet && slots.sheetSlotId !== null) {
                const sheetId = batch.sheet.addInstance(slots.sheetSlotId);
                batch.sheet.setMatrixAt(sheetId, identityMatrix);
                batch.sheet.setVisibleAt(sheetId, false);
                batchIds.sheetId = sheetId;
                ctx.sheetBatchToInstance?.set(sheetId, articleInstanceId);
            }

            if (batch.holes && slots.holesSlots.length > 0) {
                for (const holesSlot of slots.holesSlots) {
                    const holesId = batch.holes.addInstance(holesSlot.slotId);
                    batch.holes.setMatrixAt(holesId, identityMatrix);
                    batch.holes.setVisibleAt(holesId, false);
                    batchIds.holesIds.push(holesId);
                    ctx.holesBatchToInstance?.set(holesId, articleInstanceId);
                }
            }

            ctx.instanceBatchIds.set(articleInstanceId, batchIds);
        } catch (e) {
            console.warn(`[${target}] Failed to add batch instances for instance ${articleInstanceId}:`, e);
        }
    }
}

export function removeInstancesFromBatchedMesh(
    ctx: BatchedMeshMaps,
    articleInstanceIds: string[],
    target: BatchedMeshTarget
): void {
    const { meshes: batch } = ctx;
    if (!batch.physical) {
        return;
    }

    for (const articleInstanceId of articleInstanceIds) {
        const batchIds = ctx.instanceBatchIds.get(articleInstanceId);
        if (!batchIds) {
            continue;
        }

        try {
            for (const physId of batchIds.physicalIds) {
                batch.physical.deleteInstance(physId);
                ctx.physicalBatchToInstance?.delete(physId);
            }

            if (batch.sheet && batchIds.sheetId !== null) {
                batch.sheet.deleteInstance(batchIds.sheetId);
                ctx.sheetBatchToInstance?.delete(batchIds.sheetId);
            }

            if (batch.holes) {
                for (const holesId of batchIds.holesIds) {
                    batch.holes.deleteInstance(holesId);
                    ctx.holesBatchToInstance?.delete(holesId);
                }
            }

            ctx.instanceBatchIds.delete(articleInstanceId);
        } catch (e) {
            console.warn(`[${target}] Failed to remove batch instances for instance ${articleInstanceId}:`, e);
        }
    }
}

/**
 * Shared logic for updating BatchedMesh instance transforms, colors, and visibility.
 * Used by both main canvas and navigator batch updates.
 */
export function updateBatchedMeshInstance(params: {
    physicalBatch: BatchedMesh | null;
    sheetBatch: BatchedMesh | null;
    holesBatch: BatchedMesh | null;
    batchIds: InstanceBatchIds;
    slots: ArticleGeometrySlots;
    instance: ArticleInstance;
    showSelection: boolean;
    isArticleInstanceSelected: boolean;
    isArticleInstanceHovered?: boolean;
    isVisible: boolean;
    selectionColor: Color;
    defaultColor: Color;
    tmpQuaternion: Quaternion;
    tmpMatrix: Matrix4;
    scaleHidden: Vector3;
    updatedPhysical: Set<number>;
    updatedSheet: Set<number>;
    updatedHoles: Set<number>;
    lodLevel?: number;
    setBatchInstanceOpacity?: (batch: BatchedMesh, id: number, opacity: number) => void;
}) {
    const {
        physicalBatch,
        sheetBatch,
        holesBatch,
        batchIds,
        slots,
        instance,
        showSelection,
        isArticleInstanceSelected,
        isArticleInstanceHovered,
        isVisible,
        selectionColor,
        defaultColor,
        tmpQuaternion,
        tmpMatrix,
        scaleHidden,
        updatedPhysical,
        updatedSheet,
        updatedHoles,
        lodLevel,
        setBatchInstanceOpacity
    } = params;

    const isSelectedHighlight = showSelection && isArticleInstanceSelected;
    const isHoverOnlyHighlight = showSelection && !isSelectedHighlight && !!isArticleInstanceHovered;

    // Physical
    if (physicalBatch && batchIds.physicalIds && slots.physicalSlots) {
        const sourceTypeColor = getSourceTypeDebugColor(instance.sourceType);
        const instanceOpacity = isSelectedHighlight ? 0.8 : isHoverOnlyHighlight ? 0.5 : 1.0;

        for (let i = 0; i < batchIds.physicalIds.length; i++) {
            const physId = batchIds.physicalIds[i];
            const partName = slots.physicalSlots[i]?.partName;
            const baseColor = sourceTypeColor ?? slots.physicalSlots[i]?.color ?? defaultColor;
            const instanceColor = isSelectedHighlight ? selectionColor : isHoverOnlyHighlight ? hoverColor : baseColor;
            const part = partName ? instance.parts[partName] : Object.values(instance.parts)[0];
            if (!part) {
                continue;
            }
            updateFullQuaternion(part.alignment, part.rotation, tmpQuaternion);
            tmpMatrix.compose(part.position, tmpQuaternion, isVisible ? part.scale : scaleHidden);
            const targetLodLevel = Math.max(0, Math.min(MAX_LOD_LEVELS - 1, lodLevel ?? 0));
            const targetGeometryId = slots.physicalSlots[i]?.lodSlotIds?.[targetLodLevel] ?? null;
            const hasTargetLod = typeof targetGeometryId === 'number';
            const geometrySetter = physicalBatch as unknown as {
                setGeometryIdAt?: (instanceId: number, geometryId: number) => void;
            };
            if (hasTargetLod && typeof geometrySetter.setGeometryIdAt === 'function') {
                geometrySetter.setGeometryIdAt(physId, targetGeometryId);
            }
            physicalBatch.setMatrixAt(physId, tmpMatrix);
            physicalBatch.setVisibleAt(physId, isVisible && hasTargetLod);
            physicalBatch.setColorAt(physId, instanceColor);
            if (setBatchInstanceOpacity) {
                setBatchInstanceOpacity(physicalBatch, physId, instanceOpacity);
            }
            updatedPhysical.add(physId);
        }
    }

    // Sheet
    if (sheetBatch && batchIds.sheetId !== null && slots.sheetLodSlotIds && slots.sheetLodSlotIds.length > 0) {
        const targetLodLevel = Math.max(0, Math.min(MAX_LOD_LEVELS - 1, lodLevel ?? 0));
        const targetGeometryId = slots.sheetLodSlotIds[targetLodLevel] ?? null;
        const hasTargetLod = typeof targetGeometryId === 'number';

        const sheetPart = slots.sheetPartName ? instance.parts[slots.sheetPartName] : Object.values(instance.parts)[0];
        if (sheetPart) {
            updateFullQuaternion(sheetPart.alignment, sheetPart.rotation, tmpQuaternion);
            const shouldHide = showSelection && (isSelectedHighlight || isHoverOnlyHighlight);
            tmpMatrix.compose(
                sheetPart.position,
                tmpQuaternion,
                isVisible && !shouldHide ? sheetPart.scale : scaleHidden
            );

            const geometrySetter = sheetBatch as unknown as {
                setGeometryIdAt?: (instanceId: number, geometryId: number) => void;
            };
            if (hasTargetLod && typeof geometrySetter.setGeometryIdAt === 'function') {
                geometrySetter.setGeometryIdAt(batchIds.sheetId, targetGeometryId);
            }

            sheetBatch.setMatrixAt(batchIds.sheetId, tmpMatrix);
            sheetBatch.setVisibleAt(batchIds.sheetId, isVisible && !shouldHide && hasTargetLod);
            updatedSheet.add(batchIds.sheetId);
        }
    }

    // Holes: show LOD 0 for LOD 0 or 1, show nothing for LOD 2
    if (holesBatch && slots.holesSlots.length > 0 && batchIds.holesIds.length > 0) {
        for (let i = 0; i < slots.holesSlots.length; i++) {
            const holesSlot = slots.holesSlots[i];
            const holesId = batchIds.holesIds[i];
            if (holesId === undefined) {
                continue;
            }

            const holesPart = instance.parts[holesSlot.partName] ?? Object.values(instance.parts)[0];
            if (!holesPart) {
                continue;
            }

            updateFullQuaternion(holesPart.alignment, holesPart.rotation, tmpQuaternion);
            tmpMatrix.compose(holesPart.position, tmpQuaternion, isVisible ? holesPart.scale : scaleHidden);
            const targetLodLevel = Math.max(0, Math.min(MAX_LOD_LEVELS - 1, lodLevel ?? 0));
            let targetGeometryId: number | null = null;
            if (targetLodLevel === 0 || targetLodLevel === 1) {
                targetGeometryId = holesSlot.lodSlotIds[0] ?? null;
            }

            const hasGeometry = typeof targetGeometryId === 'number';
            const geometrySetter = holesBatch as unknown as {
                setGeometryIdAt?: (instanceId: number, geometryId: number) => void;
            };
            if (targetGeometryId != null && hasGeometry && typeof geometrySetter.setGeometryIdAt === 'function') {
                geometrySetter.setGeometryIdAt(holesId, targetGeometryId);
            }
            holesBatch.setMatrixAt(holesId, tmpMatrix);
            holesBatch.setVisibleAt(holesId, isVisible && hasGeometry);
            updatedHoles.add(holesId);
        }
    }
}

/**
 * Partial buffer updates for BatchedMesh - only update changed indices.
 */
export function updateAttributeRange(
    batch: BatchedMesh | null,
    indices: Set<number>,
    attrName: string,
    itemSize: number
) {
    if (!batch || indices.size === 0) {
        return;
    }
    const min = Math.min(...indices);
    const max = Math.max(...indices);

    // These are internal attributes, not in BatchedMesh's type
    const attr = (
        batch as unknown as Record<string, { updateRange?: { offset: number; count: number }; needsUpdate?: boolean }>
    )[attrName];
    if (
        attr &&
        attr.updateRange &&
        typeof attr.updateRange.offset === 'number' &&
        typeof attr.updateRange.count === 'number'
    ) {
        attr.updateRange.offset = min * itemSize;
        attr.updateRange.count = (max - min + 1) * itemSize;
        if (typeof attr.needsUpdate === 'boolean') {
            attr.needsUpdate = true;
        }
    }
}
